import { Injectable } from '@angular/core';
import { EMPTY, Observable, expand, filter, map, reduce } from 'rxjs';

import { ConfigurationService } from '@sondermind/configuration';
import { HttpService } from '@sondermind/http';
import { IAPIListRequestOptions, IAPIListResponse } from '@sondermind/utilities/models-http';

import { camelCaseTransformer } from '@sondermind/utilities/tools';

import {
  Provider,
  IProviderApiResponse
} from '../models/provider.model';

import { IProviderPNCInfo, IProviderPNCAPIResponse } from '../models/provider-pnc-ids.model';

@Injectable({
  providedIn: 'root'
})
export class ProviderHttpService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpService
  ) {}

  get indexUrl(): string {
    return `${this.configurationService.env.svcBase}/provider_profiles/api/v1/providers`;
  }

  /**
   * Fetches a specific provider by account UUID from the API
   * @param accountUuid The account UUID of the provider to fetch
   * @param opts Optional request options
   * @returns Observable of Provider
   */
  fetchByAccountUuid(opts: Partial<IAPIListRequestOptions> = {}): Observable<Provider> {
    const params = this.http.generateListParams(opts);

    return this.http
      .get<IAPIListResponse<IProviderApiResponse>>(this.indexUrl, params)
      .pipe(
        map((resp) => new Provider(resp.data[0])),
        this.http.handleHttpErrorStrict()
      );
  }

  list$(opts: Partial<IAPIListRequestOptions>): Observable<Provider[]> {
    return this.fetchPage(opts).pipe(
      expand((response) => (response.meta.links.next
        ? this.fetchPageByUrl(response.meta.links.next)
        : EMPTY)
      ),
      // Collect all pages and flatten the data arrays
      reduce((allProviders: Provider[], response) => allProviders.concat(response.data), [])
    );
  }

  getPNCInfo$(
    providerPersonaIds: number[],
    opts: Partial<IAPIListRequestOptions> = {}
  ): Observable<IProviderPNCInfo[]> {
    const apiUrl = `${this.indexUrl}/provider_network_companies`;
    let params = this.http.generateListParams(opts);
    providerPersonaIds.forEach((id) => { params = params.append('persona_provider_ids[]', id.toString()) });

    return this.http
      .get<IAPIListResponse<IProviderPNCAPIResponse[]>>(apiUrl, params)
      .pipe(
        map((jsonRes) => {
          const transformedData = camelCaseTransformer<IProviderPNCAPIResponse[]>(jsonRes.data);
          return transformedData.map((item) => ({
            personaProviderId: item.personaProviderId,
            pncIds: item.activeMatchingNetworks.map((network) => network.id)
          }));
        }),
        this.http.handleHttpErrorStrict()
      );
  }

  /**
   * Fetches a single page of providers
   * @param opts Optional request options
   * @returns Observable of IAPIListResponse with Providers
   */
  fetchPage(opts: Partial<IAPIListRequestOptions> = {}): Observable<IAPIListResponse<Provider>> {
    const params = this.http.generateListParams(opts);

    return this.http
      .get<IAPIListResponse<IProviderApiResponse>>(this.indexUrl, params)
      .pipe(
        map((resp) => ({
          ...resp,
          data: resp.data.map((provider) => new Provider(provider))
        })),
        this.http.handleHttpError(),
        filter((resp): resp is IAPIListResponse<Provider> => !!resp)
      );
  }

  /**
   * Fetches a page by direct URL (used for pagination)
   * @param url The full URL to fetch
   * @returns Observable of IAPIListResponse with Providers
   */
  private fetchPageByUrl(url: string): Observable<IAPIListResponse<Provider>> {
    return this.http
      .get<IAPIListResponse<IProviderApiResponse>>(url)
      .pipe(
        map((resp) => ({
          ...resp,
          data: resp.data.map((provider) => new Provider(provider))
        })),
        this.http.handleHttpError(),
        filter((resp): resp is IAPIListResponse<Provider> => !!resp)
      );
  }
}
